import { type CollectionSummaryJSONObject, SystemTag, type TagJSONObject } from '@ikegps/core-api';
import { IkeConfigStore } from '@ikegps/core-shared';
import { clamp, get } from 'lodash';
import { action, computed, type IObservableArray, observable, reaction, makeObservable } from 'mobx';
import { NotificationService } from '../components/notification.toast/notification.service';
import { T } from '../i18n';
import { TagSortState } from '../state/tag.sort.state';
import { AppStore } from '../store';
import { type IkeClientService, Services } from './index';
export class FilterState {
	@observable filter: string | null = null;
	tagFilters = observable.array<TagJSONObject>([], { deep: false });

	constructor() {
		makeObservable(this);
	}

	@action addTagFilter(tagFilter: TagJSONObject) {
		const existing = this.tagFilters.find((tag) => tag.objectId === tagFilter.objectId);
		if (existing == null) {
			this.tagFilters.push(tagFilter);
		}

		this.filter = null;
	}

	@action setFilter(value: string) {
		this.filter = value;
	}

	hasFilter(): boolean {
		return this.tagFilters.length > 0 || (this.filter != null && this.filter.trim().length > 0);
	}

	matches(tag: TagJSONObject) {
		if (tag == null) {
			return false;
		}

		if (!this.hasFilter()) {
			return true;
		}

		if (this.tagFilters.length > 0) {
			return this.tagFilters.find((t) => t.objectId === tag.objectId) != null;
		}

		return tag.name.toLowerCase().includes(this.filter.trim().toLowerCase());
	}

	@action clear() {
		this.filter = null;
		this.tagFilters.clear();
	}
}

const dynamicDebounce = (debouncedAction: () => void, getDebounceMs: () => number) => {
	let cancelableTimeout: ReturnType<typeof setTimeout>;

	return () => {
		if (cancelableTimeout) {
			clearTimeout(cancelableTimeout);
		}

		cancelableTimeout = setTimeout(() => {
			debouncedAction();
		}, getDebounceMs());
	};
};

const MAXIMUM_DELAY_MS = 2000;
const MINIMUM_DELAY_MS = 500;
export class SearchService implements IkeClientService {
	__priority = 100;

	inputfilterState: FilterState;
	filterState: FilterState;

	@observable inFlight: boolean = false;
	@observable collectionRevisions: IObservableArray<string> = observable.array<string>([], { deep: false });

	getDebounceDelay() {
		const charLength = get(this.inputfilterState, ['filter', 'length']);
		const evaluatedDebounce = charLength ? MAXIMUM_DELAY_MS - (charLength - 1) * 200 : MINIMUM_DELAY_MS;

		return clamp(evaluatedDebounce, MINIMUM_DELAY_MS, MAXIMUM_DELAY_MS);
	}

	constructor() {
		makeObservable(this);

		this.inputfilterState = new FilterState();
		this.filterState = new FilterState();
	}

	init() {
		const search = dynamicDebounce(this.search.bind(this), this.getDebounceDelay.bind(this));

		reaction(
			(_) => this.inputfilterState.filter,
			() => {
				if (get(this.inputfilterState, ['filter', 'length']) >= IkeConfigStore.MAX_SEARCH_LENGTH) {
					this.setInFlight(false);
					NotificationService.warn({ key: T.SEARCH_ERROR });
				} else {
					this.setInFlight(true);
					search();
				}
			}
		);
		reaction(
			(_) => Services.Department.current,
			(_) => this.reset()
		);
	}

	reset() {
		this.inputfilterState.clear();
		this.filterState.clear();
	}

	includesTag(tag: TagJSONObject): boolean {
		if (this.filterState.hasFilter()) {
			return this.tags.indexOf(tag) > -1;
		}
		return true;
	}

	@computed get tags(): TagJSONObject[] {
		if (this.filterState.hasFilter()) {
			return AppStore.Tag.all.filter(
				(tag: TagJSONObject) => this.filterState.matches(tag) || this.collectionSummaries[tag.objectId] != null
			);
		}

		return AppStore.Tag.all;
	}

	@computed get collectionSummaries(): { [tagId: string]: CollectionSummaryJSONObject[] } {
		const map: { [tagId: string]: CollectionSummaryJSONObject[] } = {};

		for (const collectionRevision of this.collectionRevisions) {
			const collectionSummary = AppStore.CollectionSummary.get(collectionRevision);
			if (collectionSummary == null) {
				continue;
			}

			if (collectionSummary.tags.indexOf(SystemTag.deleted) > -1) {
				continue;
			}

			for (const tag of collectionSummary.tags) {
				var mapTag = map[tag];
				if (mapTag == null) {
					map[tag] = [collectionSummary];
				} else {
					mapTag.push(collectionSummary);
				}
			}
		}

		for (const tagId of Object.keys(map)) {
			const summaries = map[tagId];
			TagSortState.sortSummaries(summaries);
		}

		return map;
	}

	@action setInFlight(inFlight: boolean) {
		this.inFlight = inFlight;
	}

	@action search(): Promise<void> {
		if (!this.inputfilterState.hasFilter()) {
			this.collectionRevisions.clear();
			this.filterState.setFilter(this.inputfilterState.filter);
			this.setInFlight(false);

			return Promise.resolve();
		}

		this.setInFlight(true);

		const filter = this.inputfilterState.filter;
		return AppStore.Collection.search(filter).then((results) => {
			// Old query
			if (filter !== this.inputfilterState.filter) {
				return null;
			}

			const revisions = results.map((result) => result.revision);
			return Promise.all(revisions.map((revision) => this.prefetchCollectionSummary(revision))).then(() =>
				this.setCollectionRevisions(filter, revisions)
			);
		});
	}

	@action private setCollectionRevisions(filter: string, collectionRevisions: string[]) {
		this.collectionRevisions.replace(collectionRevisions);
		this.filterState.setFilter(filter);
		this.setInFlight(false);
	}

	private prefetchCollectionSummary(
		revision: string
	): CollectionSummaryJSONObject | Promise<CollectionSummaryJSONObject> {
		const collectionSummary = AppStore.CollectionSummary.get(revision);
		if (collectionSummary != null) {
			return collectionSummary;
		}

		return AppStore.CollectionSummary.fetch(revision);
	}
}
